import { useCallback, useContext, useEffect, useRef } from 'react';
import { Box, Container, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { InstanceContext } from '@/contexts/InstanceContext';
import { ActivityContext } from '@/contexts/ActivityContext';
import { useCharacter } from '@/contexts/CharacterContext';
import { EmbeddedCompanion } from '@/components/character';
import {
  TimerCircle,
  useChildTokens,
  MissedActionsBar,
  getChildActivityStatus,
  getNextSubtask,
} from '@/components/child';
import { isNextUpEnabled } from '@/utils/npfNudges';
import { getCurrentStep, getRoutineSteps, isLockedUntilItsDay, isRoutine } from '@/utils/routine';
import RoutineStepCard from '@/components/routine/RoutineStepCard';
import { SubtaskItem } from '@api';

/**
 * ActivityInProgress — barnvyns pågående-aktivitet (prototypens PROGRESS).
 * TimerCircle (conic-gradient, drivs av useTimerState via TimerCircle), delsteg
 * (toggla via InstanceContext) och en stor "Klar"-knapp som markerar aktiviteten
 * klar och leder vidare till firande-vyn.
 *
 * Kompanjonens mood styrs av timer-status: pågår → thinking, pausad → idle. När
 * ett delsteg bockas: happy 1s → thinking. All TTS/bubbeltext sätts på
 * companionSpeech; ingen egen setInterval byggs (timern återanvänder useTimerState).
 */
const ActivityInProgress = () => {
  const { t } = useTranslation();
  const tokens = useChildTokens();
  const navigate = useNavigate();

  const {
    instance,
    isStarted,
    timerPaused,
    setTimerPaused,
    startInstance,
    completeInstance,
    toggleSubtask,
  } = useContext(InstanceContext);
  const { activity } = useContext(ActivityContext);
  const { setMood, setCompanionSpeech } = useCharacter();

  const isRunning = isStarted && !timerPaused;

  // Håll subtask-feedback-timern i ref så den rensas vid ny bock OCH vid unmount
  // (bocka delsteg + tryck Klar inom 1s → annars setState på avmonterad vy).
  const subtaskTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (subtaskTimerRef.current !== null) {
        window.clearTimeout(subtaskTimerRef.current);
      }
    },
    [],
  );

  // Mood + bubbla speglar timer-status. Nollställs vid unmount.
  useEffect(() => {
    if (isRunning) {
      setMood('thinking');
      setCompanionSpeech(t('child.companion.timerRunning') ?? '');
    } else {
      setMood('idle');
      setCompanionSpeech(t('child.companion.timerPaused') ?? '');
    }
    return () => {
      setCompanionSpeech('');
      setMood('idle');
    };
  }, [isRunning, setMood, setCompanionSpeech, t]);

  // Dagslåset: en låst aktivitet startas inte före sin dag. Backend håller regeln, så
  // spärren här är till för att brukaren ska slippa trycka på något som ändå inte
  // händer — och för att få veta varför.
  const lockedUntilItsDay = isLockedUntilItsDay(activity, instance);

  const handleToggleTimer = useCallback(() => {
    if (lockedUntilItsDay) return;
    if (!isStarted) {
      startInstance();
      return;
    }
    setTimerPaused(!timerPaused);
  }, [lockedUntilItsDay, isStarted, startInstance, timerPaused, setTimerPaused]);

  const handleToggleSubtask = useCallback(
    (subtaskId: number) => {
      toggleSubtask(subtaskId);
      // Delsteg bockat → happy 1s → thinking (positiv förstärkning).
      setMood('happy');
      setCompanionSpeech(t('child.companion.subtaskDone') ?? '');
      if (subtaskTimerRef.current !== null) {
        window.clearTimeout(subtaskTimerRef.current);
      }
      subtaskTimerRef.current = window.setTimeout(() => {
        setMood('thinking');
        setCompanionSpeech(
          (isRunning ? t('child.companion.timerRunning') : t('child.companion.timerPaused')) ?? '',
        );
        subtaskTimerRef.current = null;
      }, 1000);
    },
    [toggleSubtask, setMood, setCompanionSpeech, t, isRunning],
  );

  const handleDone = useCallback(() => {
    completeInstance();
    // Stanna på samma route — route-switchen visar Celebration när completed sätts.
  }, [completeInstance]);

  const isChecked = (subtaskId: number): boolean =>
    !!instance.checkedSubtasks?.some((i) => i === subtaskId);

  const subtasks: SubtaskItem[] = activity?.subtasks ?? [];
  // Timern får INTE längre aktivitetens längdfärg (getActivityColor målar 15/30/45
  // minuter i olika kulörer). Klockan ska följa det valda färgschemat, och det gör den
  // genom att falla tillbaka på temats accent inne i TimerCircle — samma färg som
  // förhandsvisningen i handledarens inställning visar.

  // Steg-1-stöd (Pipeline 3, igångsättning): en mjuk hint som pekar ut nästa
  // obockade delsteg. Försvinner när alla är bockade (getNextSubtask → null).
  // Gate:as på samma flagga som NextUp; läses live (men kräver ej remontering).
  const nextSubtask = getNextSubtask(subtasks, instance.checkedSubtasks ?? []);
  const showStartNudge = isNextUpEnabled() && subtasks.length > 0 && nextSubtask !== null;

  // Rutin: ett steg i taget i stället för timer + hela delstegslistan. Kravet är att
  // bara EN sak ska vara synlig, så hint, checklista och den stora Klar-knappen står
  // tillbaka för stegkortet — dess Nästa/Klar är vägen framåt. Saknas ett aktuellt
  // steg (alla avbockade utan att rutinen hunnit markeras klar) faller vyn tillbaka
  // på det vanliga utseendet, så att Klar alltid går att nå.
  const routineSteps = isRoutine(activity) ? getRoutineSteps(activity) : [];
  const showRoutineStep = getCurrentStep(routineSteps, instance.checkedSubtasks) !== null;

  return (
    <Container sx={{ pb: 4 }}>
      <Stack spacing={2} pt={1.5}>
        <EmbeddedCompanion variant="row" accent="primary" />

        <Typography sx={{ fontWeight: 900, fontSize: '1.5rem', color: tokens.text }}>
          {instance.activityName}
        </Typography>

        {getChildActivityStatus(instance) === 'missed' && <MissedActionsBar instance={instance} />}

        {/* Låst till sin dag. Rutan står FÖRE timern/stegkortet: det som förklarar varför
            ingenting händer måste synas innan man trycker, inte efter. */}
        {lockedUntilItsDay && (
          <Box
            sx={{
              bgcolor: tokens.mutedLight,
              border: `1px solid ${tokens.border}`,
              borderRadius: '14px',
              p: '12px 16px',
            }}
          >
            <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: tokens.text }}>
              🔒 {t('routine.dayLockedTitle')}
            </Typography>
            <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: tokens.text, mt: 0.25 }}>
              {t('routine.dayLockedBody')}
            </Typography>
          </Box>
        )}

        {showRoutineStep ? (
          <RoutineStepCard showName={false} />
        ) : (
          <TimerCircle
            minutes={instance.duration ?? 0}
            isRunning={isRunning}
            timerId={instance.activityInstanceId}
            onToggle={handleToggleTimer}
          />
        )}

        {!showRoutineStep && showStartNudge && nextSubtask && (
          <Box
            sx={{
              bgcolor: tokens.primaryLight,
              borderRadius: '14px',
              p: '12px 16px',
            }}
          >
            <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: tokens.text }}>
              👉 {t('child.progress.startNudgeTitle')}
            </Typography>
            <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: tokens.text, mt: 0.25 }}>
              {t('child.progress.startNudgeBody', { step: nextSubtask.subtaskText })}
            </Typography>
          </Box>
        )}

        {!showRoutineStep && subtasks.length > 0 && (
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: tokens.text, mb: 1 }}>
              ✅ {t('child.progress.subtasksTitle')}
            </Typography>
            <Stack spacing={1}>
              {subtasks.map((sub) => {
                const checked = isChecked(sub.subtaskId);
                return (
                  <Box
                    key={sub.subtaskId}
                    component="button"
                    type="button"
                    onClick={() => handleToggleSubtask(sub.subtaskId)}
                    aria-pressed={checked}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1.5,
                      bgcolor: tokens.card,
                      border: '1px solid',
                      borderColor: tokens.border,
                      borderRadius: '12px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      width: '100%',
                      fontFamily: 'inherit',
                      transition: 'background 0.15s',
                      '@media (hover: hover)': { '&:hover': { bgcolor: tokens.primaryLight } },
                    }}
                  >
                    <Box
                      aria-hidden
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        border: '2px solid',
                        borderColor: checked ? 'success.main' : tokens.border,
                        bgcolor: checked ? 'success.main' : 'transparent',
                        color: 'success.contrastText',
                        display: 'grid',
                        placeItems: 'center',
                        flexShrink: 0,
                        fontSize: '0.75rem',
                        fontWeight: 900,
                      }}
                    >
                      {checked ? '✓' : ''}
                    </Box>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        color: checked ? tokens.textSoft : tokens.text,
                        textDecoration: checked ? 'line-through' : 'none',
                      }}
                    >
                      {sub.subtaskText}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1.25, mt: 1 }}>
          <Box
            component="button"
            type="button"
            onClick={() => navigate('/')}
            sx={{
              flex: 1,
              p: '14px',
              borderRadius: '14px',
              bgcolor: 'transparent',
              color: 'primary.main',
              border: '2px solid',
              borderColor: 'primary.main',
              fontWeight: 800,
              fontSize: '1rem',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            ← {t('child.progress.back')}
          </Box>
          {/* Klar döljs för en låst dag: kunde man klara av morgondagens aktivitet i dag
              vore låset verkningslöst — man hade bara hoppat över starten. */}
          {!showRoutineStep && !lockedUntilItsDay && (
            <Box
              component="button"
              type="button"
              onClick={handleDone}
              sx={{
                flex: 1,
                p: '14px',
                borderRadius: '14px',
                bgcolor: 'success.main',
                color: 'success.contrastText',
                border: 'none',
                fontWeight: 800,
                fontSize: '1rem',
                cursor: 'pointer',
                fontFamily: 'inherit',
                '&:active': { transform: 'scale(0.98)' },
              }}
            >
              {t('child.progress.done')} ✓
            </Box>
          )}
        </Box>
      </Stack>
    </Container>
  );
};

export default ActivityInProgress;
